import React, { useState } from "react";
import Cell from "../Cell";
import ExtrasModal from "../ExtrasModal";
import { Popover, PopoverHeader, PopoverBody, UncontrolledTooltip } from "reactstrap";

const ExtrasCell = ({
  type,
  rowId,
  remarks = "",
  attachments = [],
  orderItem,
  tableManager,
  sectionId,
  isFiller = false,
  projectId,
  groupId,
  mutateCurrentRoom,
  replaceModule,
  refreshProjectSummary,
  viewMode = "default",
}) => {
  const additionalJobs = orderItem?.additional_jobs || {};
  const extras = additionalJobs?.extras || [];
  const components = additionalJobs?.components || [];
  const isActive = tableManager.isActive(type, rowId, "extras");
  const [popoverOpen, setPopoverOpen] = useState({});

  // Get component by uniq_id
  const getComponentByUniqId = (uniqId) => {
    return components.find(c => c.component_uniq_id === uniqId);
  };


  const getAttachmentsForExtra = (extra) => {
    if (!extra.attachment_ids || extra.attachment_ids.length === 0) {
      return [];
    }

    const optimisticAttachments = extra.optimistic_attachments || [];

    const existingAttachments = extra.attachment_ids
      .map(attId => {
        const attachment = orderItem?.attachments?.find(att =>
          att.id === attId || att.blob_id === attId || att === attId
        );
        return attachment;
      })
      .filter(Boolean);

    return [...existingAttachments, ...optimisticAttachments].map(att => ({
      filename: att.filename || att.name || "Attachment",
      url: att.url,
      label: att.label,
    }));
  };

  const hasExtras = extras.length > 0;

  // Only show modal for modules, not sections
  const showModal = isActive && orderItem && type === "module";

  // In Review mode, highlight cells with values using the brightest green color
  const getBackgroundColor = () => {
    if (viewMode === "review" && hasExtras) {
      return "#66D172"; // Brightest saturation green used in other columns
    }
    return undefined;
  };

  const togglePopover = (extraId) => {
    setPopoverOpen(prev => ({
      ...prev,
      [extraId]: !prev[extraId]
    }));
  };

  const extrasCellId = `cell_${type}_${rowId}_extras`;

  return (
    <>
      <Cell
        rowType={type}
        rowId={rowId}
        column="extras"
        status={type === "section" ? "idle" : "idle"}
        className="border-end flex-grow-1"
        customStyles={{
          width: "175px",
          backgroundColor: getBackgroundColor(),
        }}
        tableManager={tableManager}
      >
        <div className={`small text-break ${isFiller ? "text-muted" : ""}`} style={{ whiteSpace: "pre-wrap" }}>
          {extras.map((extra, idx) => {
            const component = extra.panel_locate 
              ? getComponentByUniqId(extra.panel_locate.component_uniq_id)
              : null;
            
            const componentLabel = component?.component_label || "";
            const componentType = component?.component_type || "";
            const category = extra.category || "";
            const subCategoryLabel = extra.sub_category_display_name || null;
            const remarks = extra.remarks?.trim() || "";
            const attachments = getAttachmentsForExtra(extra);
            const hasAttachments = attachments.length > 0;
            const extraId = `extra-${extra.id || idx}`;
            const downloadId = `download-${extra.id || idx}`;

            const parts = [];
            
            // Add label (component label)
            if (componentLabel) {
              parts.push(componentLabel);
            }
            
            // Add sub_category
            if (subCategoryLabel) {
              parts.push(subCategoryLabel);
            }
            
            // Add remarks
            if (remarks) {
              parts.push(remarks);
            }

            if (parts.length === 0 && !hasAttachments) return null;

            return (
              <div 
                key={extraId} 
                className={`d-flex align-items-center flex-wrap gap-1 ${idx < extras.length - 1 ? "mb-1" : ""}`}
              >
                {/* Category pill */}
                {category && category !== "other" && (
                  <span className="badge bg-secondary text-white" style={{ fontSize: "0.7rem" }}>
                    {category}
                  </span>
                )}
                
                {/* Component type pill */}
                {componentType && (
                  <span className="badge bg-secondary text-white" style={{ fontSize: "0.7rem" }}>
                    {componentType}
                  </span>
                )}

                {/* Label • sub_category • remarks */}
                {parts.length > 0 && (
                  <span className="d-inline-flex align-items-center flex-wrap">
                    {parts.map((part, partIdx) => (
                      <React.Fragment key={partIdx}>
                        <span>{part}</span>
                        {partIdx < parts.length - 1 && (
                          <span className="mx-1 text-muted" style={{ fontSize: "0.75rem" }}>•</span>
                        )}
                      </React.Fragment>
                    ))}
                  </span>
                )}

                {/* Dot after remarks if there are remarks and attachments */}
                {parts.length > 0 && hasAttachments && (
                  <span className="mx-1 text-muted" style={{ fontSize: "0.75rem" }}>•</span>
                )}

                {/* Download icon */}
                {hasAttachments && (
                  <>
                    {attachments.length === 1 ? (
                      <>
                        <a
                          id={downloadId}
                          href={attachments[0].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary"
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fa-solid fa-download"></i>
                        </a>
                        <UncontrolledTooltip target={downloadId} placement="top">
                          Download {attachments[0].label || attachments[0].filename}
                        </UncontrolledTooltip>
                      </>
                    ) : (
                      <>
                        <span
                          id={downloadId}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePopover(extraId);
                          }}
                          className="text-primary"
                          style={{ cursor: "pointer" }}
                        >
                          <i className="fa-solid fa-download"></i>
                        </span>
                        <Popover
                          placement="bottom"
                          isOpen={popoverOpen[extraId] || false}
                          target={downloadId}
                          toggle={() => togglePopover(extraId)}
                          style={{ maxWidth: "320px" }}
                          trigger="legacy"
                        >
                          <PopoverHeader className="bg-secondary text-white d-flex justify-content-between align-items-center">
                            <span>{attachments.length} attachments</span>
                            <button
                              type="button"
                              className="btn-close btn-close-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePopover(extraId);
                              }}
                              style={{ fontSize: "0.7rem", padding: "0.25rem" }}
                              aria-label="Close"
                            ></button>
                          </PopoverHeader>
                          <PopoverBody style={{ padding: "0.5rem" }}>
                            {attachments.map((att, attIdx) => (
                              <div
                                key={attIdx}
                                className="d-flex align-items-center justify-content-between p-2 border-bottom"
                                style={{
                                  cursor: "pointer",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                              >
                                <span className="small text-truncate" style={{ flex: 1, marginRight: "8px", minWidth: 0, overflow: "hidden" }}>
                                  {att.label || att.filename}
                                </span>
                                {att.url ? (
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Don't auto-close popover on download
                                    }}
                                    className="text-primary"
                                    style={{ 
                                      flexShrink: 0,
                                      marginLeft: "8px",
                                      cursor: "pointer",
                                      display: "inline-block",
                                      color: "#2C92E6",
                                      textDecoration: "none"
                                    }}
                                    title={`Download ${att.label || att.filename}`}
                                  >
                                    <i className="fa-solid fa-download" style={{ fontSize: "0.9rem" }}></i>
                                  </a>
                                ) : (
                                  <span
                                    className="text-muted"
                                    style={{ 
                                      flexShrink: 0,
                                      marginLeft: "8px",
                                      display: "inline-block",
                                      color: "#6c757d"
                                    }}
                                    title="No download available"
                                  >
                                    <i className="fa-solid fa-download" style={{ fontSize: "0.9rem" }}></i>
                                  </span>
                                )}
                              </div>
                            ))}
                          </PopoverBody>
                        </Popover>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Cell>
      {!hasExtras && (
        <UncontrolledTooltip target={extrasCellId} placement="bottom">
          CNC, Pattern or other works
        </UncontrolledTooltip>
      )}
      {showModal && (
        <ExtrasModal
          orderItem={orderItem}
          tableManager={tableManager}
          projectId={projectId}
          groupId={groupId || sectionId}
          mutateCurrentRoom={mutateCurrentRoom}
          replaceModule={replaceModule}
          refreshProjectSummary={refreshProjectSummary}
        />
      )}
    </>
  );
};

export default ExtrasCell;

